import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, reactive, ref, nextTick, onMounted } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import {
  LogData,
  LogDataReq,
  SoftDisabledLogService,
} from '@/views/dashboard/notice-manage/soft-disabled-log/soft-disabled-log.service'
import { formatTableEmpty } from '@/utils/format'
import { formatTimeStamp } from '@/utils/time'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { FormType, ObjectFormItem, DateType, FormSize } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'

type SoftDisabledQueryParams = {
  key_word: string
  start_time?: string | number
  end_time?: string | number
  handle_type?: number
  interval_type?: number
  page: number
  limit: number
  strategy_id?: string
  dateRange: [string, string]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'soft-disabled-log',
  setup(__props) {

const { t, locale } = useI18n()
const injector = useReflectiveInjector([SoftDisabledLogService])
const service = injector.get(SoftDisabledLogService)
const route = useRoute()

const defaultDate = [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
const [start_time, end_time] = useParseTime(defaultDate)

const queryParams = reactive<SoftDisabledQueryParams>({
  key_word: '',
  start_time: start_time != null ? String(start_time) : undefined,
  end_time: end_time != null ? String(end_time) : undefined,
  handle_type: undefined,
  interval_type: undefined,
  page: 1,
  limit: 20,
  strategy_id: (route.query.policy_id as string) || undefined,
  dateRange: defaultDate,
})

const tableRef = ref()

const { refreshList } = useCmTableListRefresh(tableRef)


const triggerResultLongMap = computed<Record<number, string>>(() => ({
  0: t('softwareDisabledLog.triggerResultDesc0'),
  1: t('softwareDisabledLog.triggerResultDesc1'),
  2: t('softwareDisabledLog.triggerResultDesc2'),
}))
const triggerResultShortOptions = computed(() => {
  const map = triggerResultLongMap.value
  return Object.entries(map).map(([value, label]) => ({
    label: label || value, // 兜底
    value: Number(value),
  }))
})
const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
  void locale.value
  return {
    strategy_id: {
      colOpt: { style: 'flex: 0 0 130px' },
      slot: true,
      label: t('noticeManage.triggerPolicy'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    handle_type: {
      colOpt: { span: 12 },
      label: t('noticeManage.triggerResult'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: triggerResultShortOptions.value,
    },
    interval_type: {
      colOpt: { span: 12 },
      label: t('noticeManage.bwList'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: [
        { label: t('noticeManage.blacklistFull'), value: 1 },
        { label: t('noticeManage.whitelistFull'), value: 2 },
      ],
    },
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('noticeManage.filterDateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val: [string, string] | null) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start != null ? String(start) : undefined
            queryParams.end_time = end != null ? String(end) : undefined
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('common.startDate'),
        'end-placeholder': t('common.endDate'),
        clearable: false,
      },
    },
  }
})

function buildRequestParams(overrides: Partial<LogDataReq> = {}): LogDataReq {
  const handle_type =
    queryParams.handle_type !== undefined && queryParams.handle_type !== null
      ? queryParams.handle_type
      : -1
  return {
    key_word: queryParams.key_word || undefined,
    start_time: queryParams.start_time as LogDataReq['start_time'],
    end_time: queryParams.end_time as LogDataReq['end_time'],
    handle_type,
    interval_type: queryParams.interval_type,
    page: queryParams.page,
    limit: queryParams.limit,
    strategy_id: queryParams.strategy_id || undefined,
    ...overrides,
  }
}

function dataApi(params: Record<string, unknown>) {
  const p = params as unknown as SoftDisabledQueryParams
  const handle_type =
    p.handle_type !== undefined && p.handle_type !== null ? p.handle_type : -1
  const req: LogDataReq = {
    key_word: (p.key_word as string) || undefined,
    start_time: p.start_time as LogDataReq['start_time'],
    end_time: p.end_time as LogDataReq['end_time'],
    handle_type,
    interval_type: p.interval_type,
    page: Number(params.page),
    limit: Number(params.limit),
    strategy_id: (p.strategy_id as string) || undefined,
  }
  return service.getLogData(req)
}

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: {
      label: t('noticeManage.operation'),
      width: 100,
      align: 'left',
      fixed: 'right',
    },
    isLink: true,
    actionButtons: () => [
      {
        name: t('noticeManage.rawData'),
        type: 'rawData',
        opt: { size: FormSize.Small, type: 'text', class: 'cm-link-light underline' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const longMap = triggerResultLongMap.value
  return {
    path: 'list',
    totalPath: 'total',
    height: '100%',
    bodyClass: 'el-card',
    bodyStyle: 'padding: 15px;border-radius: var(--el-border-radius-large);background: #fff',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    selection: false,
    showTip: false,
    columns: [
      {
        label: t('noticeManage.triggerTime'),
        prop: 'report_time',
        minWidth: 170,
        fixed: 'left',
        formatter: (_row: LogData, value: number) =>
          value ? formatTimeStamp(value, 'YYYY-MM-DD HH:mm:ss') : '--',
      },
      {
        label: t('noticeManage.triggerPolicy'),
        prop: 'strategy_name',
        columnOpt: { minWidth: 130, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('noticeManage.bwList'),
        prop: 'interval_type',
        minWidth: 100,
        formatter: (_row: LogData, value: number) =>
          value === 1 ? t('noticeManage.black') : value === 2 ? t('noticeManage.white') : '--',
      },
      {
        label: t('noticeManage.triggeredSoft'),
        prop: 'soft_name',
        columnOpt: { minWidth: 140, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('noticeManage.processPath'),
        prop: 'path',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('noticeManage.processMd5'),
        prop: 'md5',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('noticeManage.signature'),
        prop: 'signature',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('softwareDisabledLog.softVersion'),
        prop: 'soft_version',
        columnOpt: { minWidth: 130, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        label: t('noticeManage.triggerResult'),
        prop: 'handle_type',
        columnOpt: { minWidth: 180, 'show-overflow-tooltip': true },
        formatter: (_row: LogData, value: number) => longMap[value] ?? '--',
      },
      ...computerInfoCols(true, true, t, { ip: false, mac: false }),
    ],
  }
})

const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})

function handleAction({ type, rowItem }: { type: string; rowItem?: LogData }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem as Record<string, unknown>
    isShowRawData.value = true
  }
}

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: Partial<SoftDisabledQueryParams>) {
  if (form.dateRange?.length === 2) {
    const [s, e] = useParseTime(form.dateRange)
    queryParams.start_time = s != null ? String(s) : undefined
    queryParams.end_time = e != null ? String(e) : undefined
  }
  Object.assign(queryParams, form)
  await refreshList()
}

function handleQueryChange() {
  void refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  queryParams.handle_type = undefined
  queryParams.interval_type = undefined
  queryParams.strategy_id = undefined
  queryParams.dateRange = [...defaultDate]
  const [start, end] = useParseTime(defaultDate)
  queryParams.start_time = start != null ? String(start) : undefined
  queryParams.end_time = end != null ? String(end) : undefined
  void refreshList()
}

function download(type: 0 | 1) {
  const requestParams = buildRequestParams({ page: 0, limit: 0 })
  service.download(type, requestParams, t('noticeManage.exportFileSoftDisabledLog'))
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      api: dataApi,
      "page-option": {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"0","background-color":"transparent"},
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          "show-search": true,
          "sync-search-from-field": "key_word",
          "search-placeholder": _ctx.$t('noticeManage.searchPlaceholderComputerUserSoft'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          strategy_id: _withCtx(() => [
            _createVNode(PolicySelect, {
              width: "100%",
              size: "small",
              modelValue: queryParams.strategy_id,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
              "strategy-type": _unref(PolicyType).softwareDisabled,
              onChange: handleQueryChange
            }, null, 8, ["modelValue", "strategy-type"])
          ]),
          actions: _withCtx(() => [
            _createVNode(ExportDropdown, {
              "button-text-i18n-key": "noticeManage.exportRecord",
              onExport: download
            }),
            _createVNode(_component_router_link, {
              class: "cm-ui-ml10",
              to: "/dashboard/software-disabled"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, {
                  type: "primary",
                  size: "small",
                  plain: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('noticeManage.viewSoftwareDisabledPolicy')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "search-placeholder"])
      ]),
      _: 1
    }, 8, ["modelValue", "table-opt", "table-act-opt"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})